import {
  useState, useEffect, useCallback,
} from 'react';
import { get, isFunction, isUndefined } from 'lodash';
import useDebounce from './useDebounce';

async function defaultListEntities(client, query, conditions) {
  const result = await client.request(query);
  return get(result, 'entities', { totalCount: 0, nodes: [] });
}

async function defaultUpdateEntity(client, query, id, data) {
  const result = await client.request(query, { id: Number(id), patch: data });
  return get(result, 'updateEntity.entity');
}

async function defaultCreateEntity(client, query, data) {
  return null;
}

async function defaultRemoveEntity(client, query, id) {
  return null;
}

async function defaultRetrieveEntity(client, query, id) {
  const result = await client.request(query, { id: Number(id) });
  return get(result, 'entity');
}

export default function useCrud({
  client,
  listQuery,
  retrieveQuery,
  createQuery,
  updateQuery,
  removeQuery,
  tableColumns,
  formSchema,
  formUiSchema,
  getListItemConfig,
  baseUrl,
  getConditionsFromKeywords,
  retrieveEntity = defaultRetrieveEntity,
  createEntity = defaultCreateEntity,
  updateEntity = defaultUpdateEntity,
  removeEntity = defaultRemoveEntity,
  listEntities = defaultListEntities,
}) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [entities, setEntities] = useState({ totalCount: 0, nodes: [] });
  const [conditions, setConditions] = useState(null);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [currentEntityId, setCurrentEntityId] = useState(null);
  const [keywords, setKeywords] = useState('');
  const debouncedKeywords = useDebounce(keywords, 600);
  useEffect(() => {
    if (!isUndefined(debouncedKeywords) && isFunction(getConditionsFromKeywords)) {
      setConditions(getConditionsFromKeywords(debouncedKeywords));
    }
  }, [debouncedKeywords]);
  useEffect(() => {
    if (client && listQuery && conditions) {
      setErrorMessage(null);
      listEntities(client, listQuery, conditions)
        .then(setEntities)
        .catch(e => setErrorMessage(e.message));
    } else {
      setEntities({ totalCount: 0, nodes: [] });
    }
  }, [client, listQuery, conditions]);
  useEffect(() => {
    if (client && retrieveQuery && currentEntityId) {
      retrieveEntity(client, retrieveQuery, currentEntityId)
        .then(setCurrentEntity)
        .catch(e => setErrorMessage(e.message));
    } else {
      setCurrentEntity(null);
    }
  }, [client, retrieveQuery, currentEntityId]);

  const updateCurrentEntity = useCallback((data) => {
    if (client && updateQuery && currentEntity && data) {
      updateEntity(client, updateQuery, currentEntity.id, data)
        .then(setCurrentEntity)
        .catch(e => setErrorMessage(e.message));
    }
  }, [client, updateQuery, currentEntity]);

  const createCurrentEntity = useCallback((data) => {
    if (client && createQuery && currentEntity) {
      createEntity(client, createQuery, data)
        .then((entity) => {
          setCurrentEntityId(entity.id);
        })
        .catch(e => setErrorMessage(e.message));
    }
  }, [client, createQuery, currentEntity]);

  const removeCurrentEntity = useCallback(() => {
    if (client && removeQuery && currentEntity) {
      removeEntity(client, removeQuery, currentEntity.id)
        .then(() => setCurrentEntity(null))
        .catch(e => setErrorMessage(e.message));
    }
  }, [client, removeQuery, currentEntity]);
  return {
    errorMessage,
    entities,
    setConditions,
    currentEntity,
    createCurrentEntity,
    updateCurrentEntity,
    removeCurrentEntity,
    setCurrentEntityId,
    tableColumns,
    formSchema,
    formUiSchema,
    keywords,
    setKeywords,
    getListItemConfig(entity, ...props) {
      const config = getListItemConfig(entity, ...props);
      return {
        ...config,
        selected: !!currentEntity && currentEntity.id === entity.id,
      };
    },
    baseUrl,
  };
}
